import { subscribeToIncident, subscribeToMaintenance, subscribeToAdvisory } from "../../../services/api/subscriptions";

/**
 * Shared registry for the three single-event "watch" flows (Incident, Maintenance,
 * Advisory). Keyed by `panelData.issue_type`; MaintenancesPage passes its panel through
 * with no `issue_type`, so Maintenance must remain the fallback (see SubscribeModal and
 * EventWatchButton, both of which do `EVENT_TYPES[panelData?.issue_type] || EVENT_TYPES.Maintenance`).
 *
 * `subscribedKey` / `unsubscribeParam` let the watch toggle read the right array out of
 * GET /subscriptions/{email} and send the right param to POST /notification/unsubscribe
 * without hardcoding per-type branching at the call site.
 */
export const EVENT_TYPES = {
    Incident: {
        label: "Incident",
        pluralLabel: "Incidents",
        blurb: "status updates on this incident",
        getId: (panel) => panel?.incident_id,
        subscribedKey: "subscribed_incidents",
        unsubscribeParam: "incidentId",
        subscribe: (panel, body) => subscribeToIncident(panel?.incident_id, body),
        // Internal Jira-style ID field (what subscribed_incidents contains) vs. the
        // customer-facing ID field shown in URLs/lookup, plus the route the review
        // modal's event chips link into -- see EventSubscriptionsSection.
        idField: "incident_id",
        customIdField: "custom_incident_id",
        routeBase: "/incidents",
    },
    Maintenance: {
        label: "Maintenance",
        pluralLabel: "Maintenances",
        blurb: "status updates on this maintenance window",
        getId: (panel) => panel?.maintenance_id,
        subscribedKey: "subscribed_maintenances",
        unsubscribeParam: "maintenanceId",
        subscribe: (panel, body) => subscribeToMaintenance(panel?.maintenance_id, body),
        idField: "maintenance_id",
        customIdField: "custom_maintenance_id",
        routeBase: "/maintenance",
    },
    Advisory: {
        label: "Advisory",
        pluralLabel: "Advisories",
        blurb: "updates to this advisory",
        // The subscription module keys its advisory records by the internal Jira-style
        // `advisory_id` (SP-…), the same convention Incident/Maintenance use for their
        // single ID field -- not the customer-facing `custom_advisory_id` (ADV-…) shown
        // in URLs/lookup. Sending custom_advisory_id 404s: "advisory not found".
        getId: (panel) => panel?.advisory_id || panel?.custom_advisory_id,
        subscribedKey: "subscribed_advisories",
        unsubscribeParam: "advisoryId",
        subscribe: (panel, body) => subscribeToAdvisory(panel?.advisory_id || panel?.custom_advisory_id, body),
        idField: "advisory_id",
        customIdField: "custom_advisory_id",
        routeBase: "/advisories",
    },
};

export const getEventType = (issueType) => EVENT_TYPES[issueType] || EVENT_TYPES.Maintenance;

export default EVENT_TYPES;
